import {
  Badge,
  Box,
  Checkbox,
  Divider,
  Flex,
  Grid,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  articleWorkspaceStyles,
  copyArticleStyles,
} from '@texas/resources/theme/styles/entityWorkspace';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyArticleSectionHeading } from './CopyArticleSectionHeading';
import { VariantCard } from './CopyArticleVariantCard';
import { useCopyArticle } from './useCopyArticle';

export function CopyArticleScope() {
  const { t } = useTranslation();
  const {
    setValue,
    fieldWarnings,
    getValues,
    branchIds,
    contact,
    source,
    tags,
    targetTiers,
    variants,
  } = useCopyArticle();
  if (!source) return null;

  return (
    <Box sx={articleWorkspaceStyles.card}>
      <CopyArticleSectionHeading
        number="2"
        title={t('article.copy.scopeTitle')}
        description={t('article.copy.scopeDescription')}
      />
      <Stack spacing={5} sx={copyArticleStyles.cardBody}>
        <Box>
          <Text sx={copyArticleStyles.sectionLabel}>
            {t('article.copy.articleInformation')}
          </Text>
          <Grid sx={copyArticleStyles.informationGrid}>
            <ArticleInformationOption
              checked={contact}
              label={t('article.copy.contact')}
              onChange={(value) => setValue('contact', value)}
            >
              {source.contact ? (
                <Stack spacing={0}>
                  <Text sx={copyArticleStyles.informationName}>
                    {source.contact.name}
                  </Text>
                  <Text sx={copyArticleStyles.informationEmail} noOfLines={1}>
                    {source.contact.email}
                  </Text>
                </Stack>
              ) : (
                <EmptyArticleInformationValue
                  label={t('article.copy.noContact')}
                />
              )}
            </ArticleInformationOption>

            <ArticleInformationOption
              checked={tags}
              label={t('article.copy.tags')}
              onChange={(value) => setValue('tags', value)}
            >
              {source.tags.length > 0 ? (
                <Flex sx={copyArticleStyles.informationTags}>
                  {source.tags.map((tag) => (
                    <Box
                      key={tag.id}
                      bg={tag.color}
                      sx={
                        tag.archived
                          ? copyArticleStyles.informationTagArchived
                          : copyArticleStyles.informationTag
                      }
                      title={tag.archived ? t('general.archived') : undefined}
                    >
                      {tag.label}
                    </Box>
                  ))}
                </Flex>
              ) : (
                <EmptyArticleInformationValue
                  label={t('tags.noTagsAssigned')}
                />
              )}
            </ArticleInformationOption>

            <ArticleInformationOption
              checked={targetTiers}
              label={t('article.copy.targetTiers')}
              onChange={(value) => setValue('targetTiers', value)}
            >
              {source.targetTiers.length > 0 ? (
                <Flex sx={copyArticleStyles.informationTags}>
                  {source.targetTiers.map((tier, index) => (
                    <Badge
                      key={`${tier.quantity}-${index}`}
                      sx={
                        tier.archived
                          ? copyArticleStyles.informationTierArchived
                          : copyArticleStyles.informationTier
                      }
                      title={tier.archived ? t('general.archived') : undefined}
                      variant="outline"
                    >
                      {tier.quantity.toLocaleString()}
                    </Badge>
                  ))}
                </Flex>
              ) : (
                <EmptyArticleInformationValue
                  label={t('article.copy.noTargetTiers')}
                />
              )}
            </ArticleInformationOption>
          </Grid>
        </Box>
        <Divider />
        <Box id={fieldWarnings.branches.targetId}>
          <Flex sx={copyArticleStyles.branchesHeader}>
            <Box>
              <WarningComponent {...fieldWarnings.branches}>
                <Text sx={copyArticleStyles.branchesTitle}>
                  {t('article.copy.branches')}
                </Text>
              </WarningComponent>
              <Text sx={copyArticleStyles.branchesDescription}>
                {t('article.copy.branchesDescription')}
              </Text>
            </Box>
            <Text sx={copyArticleStyles.smallMuted}>
              {t('article.copy.selectedCount', {
                count: branchIds.length,
              })}
            </Text>
          </Flex>
          <Grid sx={copyArticleStyles.branchesGrid}>
            {source.branches.map((branch) => {
              const checked = branchIds.includes(branch.id);
              return (
                <Box
                  key={branch.id}
                  sx={
                    checked
                      ? copyArticleStyles.branchChecked
                      : copyArticleStyles.branch
                  }
                >
                  <Checkbox
                    isChecked={checked}
                    onChange={(event) =>
                      setValue(
                        'branchIds',
                        event.target.checked
                          ? [...getValues('branchIds'), branch.id]
                          : getValues('branchIds').filter(
                              (id) => id !== branch.id,
                            ),
                      )
                    }
                  >
                    <Stack spacing={0}>
                      <Text sx={copyArticleStyles.branchIdentifier}>
                        {branch.identifier}
                      </Text>
                      <Text
                        sx={
                          branch.isSource
                            ? copyArticleStyles.branchType
                            : copyArticleStyles.branchTypeNewLabel
                        }
                      >
                        {branch.isSource
                          ? t('article.copy.sourceBranch')
                          : t('article.copy.newBranch')}
                      </Text>
                    </Stack>
                  </Checkbox>
                </Box>
              );
            })}
          </Grid>
        </Box>
        <Divider />
        <Box id={fieldWarnings.variants.targetId}>
          <WarningComponent {...fieldWarnings.variants}>
            <Text sx={copyArticleStyles.sectionLabel}>
              {t('article.copy.variantInformation')}
            </Text>
          </WarningComponent>
          <Stack spacing={3}>
            {source.variants.map((variant) => (
              <VariantCard
                key={variant.id}
                variant={variant}
                isLinkedToOption={source.variants.some(
                  (sourceVariant) =>
                    sourceVariant.isProductDevelopment &&
                    sourceVariant.options.some(
                      (option) => option.linkedVariantId === variant.id,
                    ),
                )}
                selection={variants.find(
                  (item) => item.variantId === variant.id,
                )}
                branches={source.branches}
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

function ArticleInformationOption({
  checked,
  children,
  label,
  onChange,
}: {
  checked: boolean;
  children: ReactNode;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Box
      sx={
        checked
          ? copyArticleStyles.informationOptionChecked
          : copyArticleStyles.informationOption
      }
    >
      <Checkbox
        sx={copyArticleStyles.informationOptionCheckbox}
        isChecked={checked}
        onChange={(event) => onChange(event.target.checked)}
      >
        {label}
      </Checkbox>
      <Box
        sx={
          checked
            ? copyArticleStyles.informationOptionValue
            : copyArticleStyles.informationOptionInactiveValue
        }
      >
        {children}
      </Box>
    </Box>
  );
}

function EmptyArticleInformationValue({ label }: { label: string }) {
  return <Text sx={copyArticleStyles.smallMuted}>{label}</Text>;
}
